import React, { useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Spinner } from "react-bootstrap";
import Chart from "chart.js/auto";
import { ApiHook } from "../../../hooks/apiHook";

const KpiCard = ({ title, value, subtext, color }) => (
  <div className="col-lg-3 col-md-4 col-sm-6 mb-3">
    <div className="card h-100 shadow-sm border-0">
      <div className="card-body text-center">
        <h6 className="text-muted mb-1" style={{ fontSize: "12px" }}>{title}</h6>
        <h4 className="mb-0" style={{ color: color || "#333", fontWeight: "bold" }}>{value}</h4>
        {subtext && <small className="text-muted">{subtext}</small>}
      </div>
    </div>
  </div>
);

const SalesTrendChart = ({ trend, t }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (!chartRef.current || !trend || trend.length === 0) return;

    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    const ctx = chartRef.current.getContext("2d");
    chartInstance.current = new Chart(ctx, {
      type: "line",
      data: {
        labels: trend.map((t) => t.date),
        datasets: [
          {
            label: t("directSales"),
            data: trend.map((t) => t.directSales),
            borderColor: "#1cc88a",
            backgroundColor: "rgba(28, 200, 138, 0.1)",
            fill: true,
            tension: 0.3,
          },
          {
            label: t("groupSales"),
            data: trend.map((t) => t.groupSales),
            borderColor: "#4e73df",
            backgroundColor: "rgba(78, 115, 223, 0.1)",
            fill: true,
            tension: 0.3,
          },
          {
            label: t("personalPurchases"),
            data: trend.map((t) => t.personalPurchases),
            borderColor: "#f6c23e",
            backgroundColor: "rgba(246, 194, 62, 0.1)",
            fill: true,
            tension: 0.3,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: { position: "top", labels: { font: { size: 11 } } },
        },
        scales: {
          x: { ticks: { font: { size: 10 }, maxTicksLimit: 15 } },
          y: {
            type: "linear",
            display: true,
            position: "left",
            beginAtZero: true,
            ticks: { font: { size: 10 } },
          },
        },
      },
    });

    return () => {
      if (chartInstance.current) chartInstance.current.destroy();
    };
  }, [trend, t]);

  return (
    <div style={{ height: "300px" }}>
      <canvas ref={chartRef}></canvas>
    </div>
  );
};

const OverviewSection = ({ startDate, endDate }) => {
  const { t } = useTranslation();
  const { data, isLoading, isFetching, isError } = ApiHook.CallNetworkOverview(startDate, endDate);

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" size="sm" /> {t("loading")}
      </div>
    );
  }

  if (isError || !data?.data) {
    return <div className="alert alert-warning">{t("errorLoadingData")}</div>;
  }

  const { summary, trend } = data.data;

  const formatNum = (n) => {
    if (n == null || isNaN(n)) return "0";
    return Number(n).toLocaleString();
  };

  const growthIcon = summary.periodGrowth >= 0 ? "▲" : "▼";
  const growthColor = summary.periodGrowth >= 0 ? "#1cc88a" : "#e74a3b";

  return (
    <div>
      {isFetching && !isLoading && (
        <div className="text-center mb-2">
          <Spinner animation="border" size="sm" />
        </div>
      )}
      <div className="row">
        <KpiCard title={t("directSales")} value={formatNum(summary.directSales)} color="#1cc88a" />
        <KpiCard title={t("groupSales")} value={formatNum(summary.groupSales)} color="#4e73df" />
        <KpiCard title={t("personalPurchases")} value={formatNum(summary.personalPurchases)} color="#f6c23e" />
        <KpiCard
          title={t("activeSellers")}
          value={summary.activeSellers}
          subtext={`/ ${formatNum(summary.totalMembers)} ${t("members")}`}
          color="#36b9cc"
        />
      </div>
      <div className="row mb-3">
        <KpiCard
          title={t("avgSalesPerMember")}
          value={summary.avgSalesPerMember?.toFixed(2)}
          color="#858796"
        />
        <KpiCard
          title={t("periodGrowth")}
          value={
            <span style={{ color: growthColor }}>
              {growthIcon} {Math.abs(summary.periodGrowth).toFixed(1)}%
            </span>
          }
        />
        <KpiCard
          title={t("activityCount")}
          value={formatNum(summary.activityCount)}
          color="#5a5c69"
        />
      </div>

      <div className="row">
        <div className="col-12 mb-3">
          <div className="card shadow-sm border-0">
            <div className="card-body">
              <h6 className="card-title" style={{ fontSize: "14px" }}>
                {t("salesTrend")}
              </h6>
              {trend && trend.length > 0 ? (
                <SalesTrendChart trend={trend} t={t} />
              ) : (
                <p className="text-muted text-center py-4">{t("noDataAvailable")}</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverviewSection;
